(() => {
  const ROOT_SELECTOR = 'cardology-header.cdo-header';
  const DEBOUNCE_MS = 220;

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const normaliseSearchText = (value = '') => String(value)
    .toLowerCase()
    .replace(/&amp;/g, ' and ')
    .replace(/['’]s\b/g, 's')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const money = (amount, currency, locale) => {
    const numeric = Number(amount);
    if (!Number.isFinite(numeric)) return '';

    try {
      return new Intl.NumberFormat(locale || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
      }).format(numeric);
    } catch (error) {
      return `£${numeric.toFixed(2)}`;
    }
  };

  class CardologyPredictiveSearch {
    constructor(root) {
      this.root = root;
      this.overlay = root.querySelector('[data-cdo-search-overlay]');
      if (!this.overlay) return;

      this.inputs = Array.from(this.overlay.querySelectorAll('[data-cdo-search-input]'));
      this.queryFields = Array.from(this.overlay.querySelectorAll('[data-cdo-search-query]'));
      this.forms = Array.from(this.overlay.querySelectorAll('[data-cdo-search-form]'));
      this.termButtons = Array.from(this.overlay.querySelectorAll('[data-cdo-search-term]'));
      this.defaultContent = this.overlay.querySelector('[data-cdo-search-default]');
      this.results = this.overlay.querySelector('[data-cdo-search-results]');
      this.endpoint = this.overlay.dataset.cdoPredictiveUrl;
      this.searchUrl = this.overlay.dataset.cdoSearchUrl || '/search';
      this.currency = this.overlay.dataset.cdoCurrency || 'GBP';
      this.locale = this.overlay.dataset.cdoLocale || 'en-GB';
      this.aliases = this.parseAliases();
      this.abortController = null;
      this.timer = null;
      this.legacyCleanupFrame = null;

      this.bind();
      this.observeLegacySearch();
      this.initialiseReviewBadges();
    }

    parseAliases() {
      const element = this.overlay.querySelector('[data-cdo-search-aliases]');
      if (!element) return [];

      try {
        const parsed = JSON.parse(element.textContent || '[]');
        if (!Array.isArray(parsed)) return [];

        return parsed
          .filter((entry) => entry && entry.active !== false && String(entry.displayName || '').trim())
          .map((entry) => {
            const displayName = String(entry.displayName || '').trim();
            const rawTerms = Array.isArray(entry.triggerTerms)
              ? entry.triggerTerms
              : String(entry.triggerTerms || '')
                .split(/[\n,;|•]+/)
                .map((term) => term.trim())
                .filter(Boolean);

            const terms = [...rawTerms, displayName]
              .map((term) => normaliseSearchText(term))
              .filter(Boolean);

            return {
              displayName,
              terms: [...new Set(terms)],
            };
          });
      } catch (error) {
        return [];
      }
    }

    aliasQueryVariants(query) {
      const normalised = normaliseSearchText(query);
      if (!normalised) return [];

      const genericAliasTokens = new Set(['card', 'cards', 'greeting', 'greetings']);
      const stripped = normalised
        .split(' ')
        .filter((token) => !genericAliasTokens.has(token))
        .join(' ')
        .trim();

      return [...new Set([normalised, stripped].filter(Boolean))];
    }

    resolveAliases(query) {
      if (!this.aliases.length) return [];
      const variants = new Set(this.aliasQueryVariants(query));
      if (!variants.size) return [];

      return this.aliases.filter((alias) => alias.terms.some((term) => variants.has(term)));
    }

    resolvedAliasQuery(query) {
      const aliases = this.resolveAliases(query);
      if (!aliases.length) return query;

      return [...new Set(aliases.map((alias) => alias.displayName))].join(' OR ');
    }

    bind() {
      this.inputs.forEach((input) => {
        input.addEventListener('input', (event) => {
          event.stopImmediatePropagation();
          this.onInput(event);
        }, true);

        input.addEventListener('focus', (event) => {
          event.stopImmediatePropagation();
          this.suppressLegacySearch();
        }, true);

        input.addEventListener('focusin', (event) => {
          event.stopImmediatePropagation();
          this.suppressLegacySearch();
        }, true);

        input.addEventListener('keyup', (event) => {
          event.stopImmediatePropagation();
        }, true);

        input.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'Enter') {
            event.stopImmediatePropagation();
          }
          if (event.key === 'Escape') this.reset();
        }, true);
      });

      this.forms.forEach((form) => {
        form.addEventListener('submit', () => {
          const visibleInput = form.querySelector('[data-cdo-search-input]');
          const hiddenQuery = form.querySelector('[data-cdo-search-query]');
          if (visibleInput && hiddenQuery) {
            hiddenQuery.value = this.resolvedAliasQuery(visibleInput.value.trim());
          }
        });
      });

      this.termButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const term = button.dataset.cdoSearchTerm || button.textContent.trim();
          this.setQuery(term);
        });
      });

      this.overlay.addEventListener('submit', (event) => {
        const form = event.target.closest('[data-cdo-add-form]');
        if (!form) return;
        event.preventDefault();
        this.addToCart(form);
      });
    }

    observeLegacySearch() {
      this.legacyObserver = new MutationObserver(() => {
        window.cancelAnimationFrame(this.legacyCleanupFrame);
        this.legacyCleanupFrame = window.requestAnimationFrame(() => this.suppressLegacySearch());
      });

      this.legacyObserver.observe(this.overlay, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['open', 'class', 'hidden'],
      });
    }

    suppressLegacySearch() {
      this.overlay.querySelectorAll('predictive-search[open]').forEach((element) => {
        element.removeAttribute('open');
        element.removeAttribute('results');
      });

      const legacySelectors = [
        '[data-predictive-search]',
        '#predictive-search-results',
        '.predictive-search-scroll-content',
        '.predictive-search__load-results',
        '.predictive-search-status',
      ];

      this.overlay.querySelectorAll(legacySelectors.join(',')).forEach((element) => {
        if (element === this.results || element.contains(this.results)) return;
        element.hidden = true;
        element.setAttribute('aria-hidden', 'true');
        element.style.setProperty('display', 'none', 'important');
      });
    }

    initialiseReviewBadges() {
      window.requestAnimationFrame(() => {
        if (window.jdgm && typeof window.jdgm.initializeWidgets === 'function') {
          window.jdgm.initializeWidgets();
        }
      });
    }

    async hydrateReviewBadges(queries) {
      if (!this.results || !this.endpoint) return;

      const queryList = [...new Set((Array.isArray(queries) ? queries : [queries])
        .map((query) => String(query || '').trim())
        .filter(Boolean))];
      if (!queryList.length) return;

      const htmlEndpoint = this.endpoint.replace(/\.json(?=\?|$)/, '');
      const responses = await Promise.allSettled(queryList.map(async (query) => {
        const params = new URLSearchParams({
          q: query,
          'resources[type]': 'product',
          'resources[limit]': '8',
          'resources[options][unavailable_products]': 'hide',
          section_id: 'cardology-search-review-badges',
        });

        const response = await fetch(`${htmlEndpoint}?${params.toString()}`, {
          signal: this.abortController?.signal,
          headers: { Accept: 'text/html' },
        });

        if (!response.ok) return [];

        const markup = await response.text();
        const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
        return Array.from(documentFragment.querySelectorAll('[data-cdo-review-badge]'));
      }));

      const renderedBadges = responses.flatMap((result) => result.status === 'fulfilled' ? result.value : []);
      if (!renderedBadges.length) {
        this.initialiseReviewBadges();
        return;
      }

      const targets = Array.from(this.results.querySelectorAll('.cdo-header__product-rating[data-id]'));

      renderedBadges.forEach((renderedBadge) => {
        const productId = renderedBadge.dataset.productId;
        if (!productId) return;

        const target = targets.find((element) => String(element.dataset.id) === String(productId));
        if (!target || target.innerHTML.trim()) return;

        target.innerHTML = renderedBadge.innerHTML.trim();
      });

      this.initialiseReviewBadges();
    }

    async addToCart(form) {
      const button = form.querySelector('[data-cdo-add-to-cart]');
      const label = form.querySelector('[data-cdo-button-label]');
      const variantId = form.querySelector('[name="id"]')?.value;
      if (!button || !variantId || button.disabled) return;

      const originalLabel = label?.textContent || 'Add to cart';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (label) label.textContent = 'Adding…';

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });

        const payload = await response.json();
        if (!response.ok || payload.status) throw new Error(payload.description || payload.message || 'Unable to add item');

        if (label) label.textContent = 'Added';
        button.classList.add('is-added');

        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'cardology-search' } }));
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'cardology-search',
            productVariantId: variantId,
          });
        }

        window.setTimeout(() => {
          button.classList.remove('is-added');
          if (label) label.textContent = originalLabel;
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }, 1800);
      } catch (error) {
        if (label) label.textContent = 'Try again';
        button.disabled = false;
        button.removeAttribute('aria-busy');
        window.setTimeout(() => {
          if (label) label.textContent = originalLabel;
        }, 1800);
      }
    }

    syncQuery(value) {
      this.queryFields.forEach((field) => {
        field.value = value;
      });
    }

    activeInput() {
      return this.inputs.find((input) => input.offsetParent !== null) || this.inputs[0];
    }

    setQuery(term) {
      this.inputs.forEach((input) => {
        input.value = term;
      });
      this.syncQuery(term);

      const input = this.activeInput();
      if (input) input.focus({ preventScroll: true });
      this.suppressLegacySearch();
      this.fetchResults(term);
    }

    onInput(event) {
      const rawValue = event.target.value;
      const query = rawValue.trim();
      this.suppressLegacySearch();

      this.inputs.forEach((input) => {
        if (input !== event.target) input.value = rawValue;
      });
      this.syncQuery(rawValue);

      window.clearTimeout(this.timer);
      if (query.length < 2) {
        this.reset();
        return;
      }

      this.timer = window.setTimeout(() => this.fetchResults(query), DEBOUNCE_MS);
    }

    async requestSearch(query, resourceTypes = 'product,collection,page,article') {
      const params = new URLSearchParams({
        q: query,
        'resources[type]': resourceTypes,
        'resources[limit]': '8',
        'resources[options][unavailable_products]': 'hide',
        'resources[options][fields]': 'title,product_type,tag,variants.title,vendor',
      });

      const response = await fetch(`${this.endpoint}?${params.toString()}`, {
        signal: this.abortController.signal,
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
      const payload = await response.json();
      return payload?.resources?.results || {};
    }

    mergeAliasProducts(resultSets, limit = 4) {
      const pools = resultSets.map((groups) => [...(groups.products || [])]);
      const indexes = pools.map(() => 0);
      const seen = new Set();
      const merged = [];
      let madeProgress = true;

      while (merged.length < limit && madeProgress) {
        madeProgress = false;

        pools.forEach((pool, poolIndex) => {
          if (merged.length >= limit) return;

          while (indexes[poolIndex] < pool.length) {
            const product = pool[indexes[poolIndex]];
            indexes[poolIndex] += 1;
            const key = String(product?.id || product?.url || '');
            if (!key || seen.has(key)) continue;

            seen.add(key);
            merged.push(product);
            madeProgress = true;
            break;
          }
        });
      }

      return merged;
    }

    async fetchResults(query) {
      if (!this.endpoint || !this.results) return;
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.setLoading(true);

      try {
        const aliasMatches = this.resolveAliases(query);

        if (aliasMatches.length) {
          const targetNames = [...new Set(aliasMatches.map((alias) => alias.displayName))];
          const resultSets = await Promise.all(
            targetNames.map((targetName) => this.requestSearch(targetName, 'product,collection'))
          );
          const products = this.mergeAliasProducts(resultSets, 4);
          const collections = resultSets.flatMap((groups) => groups.collections || []);

          let collectionMatch = null;
          let viewAllUrl = `${this.searchUrl}?q=${encodeURIComponent(targetNames.join(' OR '))}`;

          if (targetNames.length === 1) {
            collectionMatch = this.findBestCollection(targetNames[0], resultSets[0]?.collections || []);
            viewAllUrl = collectionMatch?.url || `${this.searchUrl}?q=${encodeURIComponent(targetNames[0])}`;
          }

          this.render(query, { products, collections }, {
            collectionMatch,
            viewAllUrl,
            reviewQueries: targetNames,
            resolvedViewAll: true,
          });
        } else {
          const groups = await this.requestSearch(query);
          this.render(query, groups);
        }
      } catch (error) {
        if (error.name !== 'AbortError') this.renderError(query);
      } finally {
        this.setLoading(false);
        this.suppressLegacySearch();
      }
    }

    setLoading(loading) {
      if (!this.results) return;
      this.results.setAttribute('aria-busy', loading ? 'true' : 'false');

      if (loading) {
        this.defaultContent?.setAttribute('hidden', '');
        this.results.hidden = false;
        this.results.innerHTML = '<div class="cdo-search__loading"><span></span><span></span><span></span></div>';
      }
    }

    findBestCollection(query, collections) {
      const normalisedQuery = normaliseSearchText(query);
      if (!normalisedQuery || !collections.length) return null;

      const queryTokens = normalisedQuery.split(' ').filter((token) => token.length > 1);
      let bestMatch = null;
      let bestScore = 0;

      collections.forEach((collection) => {
        const title = normaliseSearchText(collection.title);
        const handle = normaliseSearchText((collection.url || '').split('/').filter(Boolean).pop() || '');
        let score = 0;

        if (title === normalisedQuery || handle === normalisedQuery) score = 100;
        else if (title.startsWith(normalisedQuery) || handle.startsWith(normalisedQuery)) score = 92;
        else if (title.includes(normalisedQuery) || handle.includes(normalisedQuery)) score = 86;
        else {
          const searchableTokens = new Set(`${title} ${handle}`.split(' ').filter(Boolean));
          const matchingTokens = queryTokens.filter((token) => searchableTokens.has(token)).length;
          score = Math.round((queryTokens.length ? matchingTokens / queryTokens.length : 0) * 75);
        }

        if (score > bestScore) {
          bestScore = score;
          bestMatch = collection;
        }
      });

      return bestScore >= 60 ? bestMatch : null;
    }

    render(query, groups, options = {}) {
      const products = groups.products || [];
      const collections = groups.collections || [];
      const hasForcedCollectionMatch = Object.prototype.hasOwnProperty.call(options, 'collectionMatch');
      const collectionMatch = hasForcedCollectionMatch
        ? options.collectionMatch
        : this.findBestCollection(query, collections);
      const viewAllUrl = options.viewAllUrl || collectionMatch?.url || `${this.searchUrl}?q=${encodeURIComponent(query)}`;
      const reviewQueries = options.reviewQueries || [query];
      const resolvedViewAllAttribute = options.resolvedViewAll ? ' data-cdo-resolved-view-all="true"' : '';

      this.defaultContent?.setAttribute('hidden', '');
      this.results.hidden = false;

      if (!products.length) {
        const collectionTitle = collectionMatch?.title;
        this.results.innerHTML = `
          <div class="cdo-search__empty">
            <p class="cdo-header__results-eyebrow">No exact card match</p>
            <h3>${collectionTitle ? `Explore ${escapeHtml(collectionTitle)}` : `We couldn’t find “${escapeHtml(query)}”`}</h3>
            <p>${collectionTitle ? 'Browse the full collection for more relevant Cardology designs.' : 'Try a broader occasion, recipient, character or card style.'}</p>
            <a class="cdo-search__primary-cta" href="${escapeHtml(viewAllUrl)}">${collectionTitle ? 'View collection' : 'Search all Cardology'}</a>
          </div>`;
        return;
      }

      this.results.innerHTML = `
        <div class="cdo-header__trending-head">
          <div>
            <p class="cdo-header__results-eyebrow">Search results</p>
            <h3>Cards matching “${escapeHtml(query)}”</h3>
          </div>
          <a href="${escapeHtml(viewAllUrl)}"${resolvedViewAllAttribute}${collectionMatch ? ` aria-label="View all ${escapeHtml(collectionMatch.title)}"` : ''}>View all</a>
        </div>
        <div class="cdo-header__product-grid">
          ${products.slice(0, 4).map((product) => this.productCard(product)).join('')}
        </div>`;

      this.hydrateReviewBadges(reviewQueries).catch(() => this.initialiseReviewBadges());
    }

    productCard(product) {
      const image = product.featured_image?.url || product.image || '';
      const imageAlt = product.featured_image?.alt || product.title || '';
      const price = product.price_min ?? product.price;
      const compareAt = product.compare_at_price_min ?? product.compare_at_price;
      const onSale = Number(compareAt) > Number(price);
      const imageUrl = image ? `${image}${image.includes('?') ? '&' : '?'}width=600` : '';
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const variant = variants.find((item) => item.available !== false) || variants[0];
      const variantId = variant?.id || product.variant_id || '';
      const productId = product.id || '';

      return `
        <article class="cdo-header__product-card">
          <a class="cdo-header__product-image" href="${escapeHtml(product.url)}" aria-label="View ${escapeHtml(product.title)}">
            ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(imageAlt)}" loading="lazy" width="600" height="600">` : ''}
          </a>
          <div class="cdo-header__product-copy">
            <a class="cdo-header__product-title" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
            <div class="cdo-header__product-meta-row">
              <div class="cdo-header__product-rating jdgm-widget jdgm-preview-badge" data-id="${escapeHtml(productId)}"></div>
              <div class="cdo-header__product-price-row">
                <span class="cdo-header__product-price">${money(price, this.currency, this.locale)}</span>
                ${onSale ? `<s class="cdo-header__product-compare-price">${money(compareAt, this.currency, this.locale)}</s>` : ''}
              </div>
            </div>
            ${variantId ? `
              <form method="post" action="/cart/add" class="cdo-header__product-form" data-cdo-add-form>
                <input type="hidden" name="id" value="${escapeHtml(variantId)}">
                <input type="hidden" name="quantity" value="1">
                <button type="submit" class="cdo-header__product-button" data-cdo-add-to-cart>
                  <span data-cdo-button-label>Add to cart</span>
                </button>
              </form>` : `<a class="cdo-header__product-button" href="${escapeHtml(product.url)}">View card</a>`}
          </div>
        </article>`;
    }

    renderError(query) {
      this.defaultContent?.setAttribute('hidden', '');
      this.results.hidden = false;
      this.results.innerHTML = `
        <div class="cdo-search__empty">
          <h3>Search is temporarily unavailable</h3>
          <p>You can still continue to the complete search results page.</p>
          <a class="cdo-search__primary-cta" href="${this.searchUrl}?q=${encodeURIComponent(this.resolvedAliasQuery(query))}">Continue search</a>
        </div>`;
    }

    reset() {
      if (this.abortController) this.abortController.abort();
      this.syncQuery('');

      if (this.results) {
        this.results.hidden = true;
        this.results.innerHTML = '';
      }

      this.defaultContent?.removeAttribute('hidden');
      this.suppressLegacySearch();
      this.initialiseReviewBadges();
    }
  }

  const initialise = (scope = document) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      if (root.dataset.cdoPredictiveReady === 'true') return;
      root.dataset.cdoPredictiveReady = 'true';
      new CardologyPredictiveSearch(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();